
import { cn } from "@/lib/utils";
import { VenueRecommendation } from "./types";
import { Globe, Instagram, MapPin } from "lucide-react";

interface VenueCardProps {
  recommendation: VenueRecommendation;
  isSelected: boolean;
  onClick: () => void;
}

export const VenueCard = ({ recommendation, isSelected, onClick }: VenueCardProps) => {
  const { venue } = recommendation;
  
  return (
    <div 
      onClick={onClick}
      className={cn(
        "h-[340px] w-full cursor-pointer rounded-lg border transition-all",
        isSelected ? "border-primary ring-2 ring-primary ring-offset-2" : "border-border hover:border-primary/50"
      )}
    >
      <div
        className="h-[160px] w-full bg-cover bg-center rounded-t-lg"
        style={{
          backgroundImage: venue.image_url 
            ? `url(${venue.image_url})` 
            : 'linear-gradient(to bottom right, #E5DEFF, #9b87f5)'
        }}
        aria-label={venue.image_url ? "Venue image" : "Venue image placeholder"}
      />
      <div className="p-4 space-y-3">
        <h3 className="font-semibold truncate">{venue.name}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{venue.address}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {venue.style && (
            <span className="px-2 py-1 bg-secondary rounded-full">{venue.style}</span>
          )}
          {venue.price_range && (
            <span className="px-2 py-1 bg-secondary rounded-full">{venue.price_range}</span>
          )}
        </div>
        
        {/* Venue links section - added from event details VenueCard */}
        <div className="flex items-center gap-2 mt-2">
          {venue.website_url && (
            <a 
              href={venue.website_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 transition-colors"
              title="Visit website"
              onClick={(e) => e.stopPropagation()} // Prevent card click when clicking link
            >
              <Globe size={16} />
            </a>
          )}
          {venue.instagram_handle && (
            <a 
              href={`https://instagram.com/${venue.instagram_handle.replace('@', '')}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 transition-colors"
              title="Visit Instagram"
              onClick={(e) => e.stopPropagation()} // Prevent card click when clicking link
            >
              <Instagram size={16} />
            </a>
          )}
          {venue.google_maps_link && (
            <a 
              href={venue.google_maps_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 transition-colors"
              title="View on Google Maps"
              onClick={(e) => e.stopPropagation()} // Prevent card click when clicking link
            >
              <MapPin size={16} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
